import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  MenuItem,
  Chip,
} from "@mui/material";
import { bulkAssignTraining, getTrainingModules } from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * Bulk assign a training module to many drivers.
 *
 * Parent component owns the list of candidate drivers (passed via `drivers` prop).
 * In the OnboardingPipelinePage, parent flattens the board into a single list.
 */
export const BulkAssignTrainingDialog = ({ open, onClose, drivers, defaultSelectedIds = [] }) => {
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set(defaultSelectedIds));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setResult(null);
    setSelected(new Set(defaultSelectedIds));
    (async () => {
      try {
        const res = await getTrainingModules();
        setModules(unwrapList(res));
      } catch (e) {
        setError(e?.message || "Failed to load training modules");
      }
    })();
  }, [open]); // intentionally not depending on defaultSelectedIds (reset on open only)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((d) => {
      const hay = `${d.fullName} ${d.email || ""} ${d.phone || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [drivers, search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (filtered.every((d) => selected.has(d.driverDetailsId))) {
      // Unselect all filtered
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((d) => next.delete(d.driverDetailsId));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((d) => next.add(d.driverDetailsId));
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!moduleId) {
      setError("Pick a training module");
      return;
    }
    if (selected.size === 0) {
      setError("Pick at least one driver");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await bulkAssignTraining({
        trainingModuleId: moduleId,
        driverDetailsIds: Array.from(selected),
      });
      setResult(unwrapObject(res));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Bulk assign failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Bulk Assign Training</DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}

        {result ? (
          <Stack spacing={1.5}>
            <Alert severity="success">
              Assigned to {result.assigned} driver(s).
              {result.skipped > 0 ? ` Skipped ${result.skipped} (already had open assignment).` : ""}
              {result.failed > 0 ? ` ${result.failed} failed.` : ""}
            </Alert>
            {result.skipped > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Skipped</Typography>
                {result.skippedDrivers?.map((s) => (
                  <Typography key={s.driverDetailsId} variant="caption" display="block" color="text.secondary">
                    • {s.driverDetailsId.slice(0, 8)}… — {s.reason}
                  </Typography>
                ))}
              </Box>
            ) : null}
            {result.failed > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }} color="error">Failures</Typography>
                {result.failures?.map((f) => (
                  <Typography key={f.driverDetailsId} variant="caption" display="block" color="error">
                    • {f.driverDetailsId.slice(0, 8)}… — {f.error}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <TextField
              select
              label="Training module"
              value={moduleId}
              onChange={(e) => setModuleId(e.target.value)}
              fullWidth
              size="small"
            >
              {modules.length === 0 ? (
                <MenuItem value="" disabled>No modules loaded</MenuItem>
              ) : (
                modules.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.title} {m.requiredByDefault ? "" : "(optional)"}
                  </MenuItem>
                ))
              )}
            </TextField>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search drivers by name / email / phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button size="small" onClick={toggleAll}>
                  {filtered.every((d) => selected.has(d.driverDetailsId)) && filtered.length > 0
                    ? "Unselect all"
                    : "Select all"}
                </Button>
                <Chip
                  size="small"
                  label={`${selected.size} selected`}
                  color={selected.size > 0 ? "primary" : "default"}
                />
              </Box>
              <Box sx={{ maxHeight: 320, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 1 }}>
                {filtered.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                    No drivers match
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {filtered.map((d, i) => (
                      <React.Fragment key={d.driverDetailsId}>
                        {i > 0 ? <Divider component="li" /> : null}
                        <ListItem
                          button
                          onClick={() => toggle(d.driverDetailsId)}
                          sx={{ py: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={selected.has(d.driverDetailsId)}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={d.fullName}
                            secondary={d.email || d.phone || "—"}
                            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                          <Chip
                            size="small"
                            label={d.currentStep?.replace(/_/g, " ")}
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {result ? (
          <Button onClick={onClose} variant="contained">Done</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !moduleId || selected.size === 0}
              startIcon={loading ? <CircularProgress size={16} /> : null}
              sx={{ backgroundColor: "#1F4E78" }}
            >
              Assign to {selected.size} driver(s)
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignTrainingDialog;
